<i18n src="@/locales/models/org_invoice.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/org_invoice_item.yml" lang="yaml"></i18n>

<template>
  <a :class="['link', link_color]" @click.prevent="open = true">
    <i :class="icon" /><span class="space-icon" />
  </a>
  <TransitionRoot as="template" :show="open">
    <Dialog
      as="div"
      class="fixed z-10 inset-0 overflow-y-auto"
      @close="open = false"
    >
      <div
        class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay
            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          />
        </TransitionChild>

        <span
          class="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
          >&#8203;</span
        >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div class="panel-modal">
            <div class="panel-modal-close">
              <button
                type="button"
                class="panel-modal-close"
                @click="open = false"
              >
                <span class="sr-only">Close</span>
                <i class="h-6 w-6 fa-solid fa-xmark"></i>
              </button>
            </div>

            <div class="panel-header-title">
              {{ org_invoice.invoice_code }}
              <yes-no-label
                :value="org_invoice.paid"
                yes="Pagada"
                no="No pagada"
              />
            </div>

            <div class="panel-modal-body">
              <div class="grid grid-cols-2 gap-x-4 gap-y-8">
                <div class="sm:col-span-1">
                  <dt class="label-data">
                    {{ $t("model.org_invoice.invoice_date") }}
                  </dt>
                  <dd class="content-data">
                    {{ dateFormatter(org_invoice.invoice_date) }}
                  </dd>
                </div>
                <div class="sm:col-span-1">
                  <dt class="label-data">
                    {{ $t("model.org_invoice.receipt_date") }}
                  </dt>
                  <dd class="content-data">
                    {{ dateFormatter(org_invoice.receipt_date) }}
                  </dd>
                </div>
                <div class="sm:col-span-1">
                  <dt class="label-data">
                    {{ $t("model.org_invoice.amount") }}
                  </dt>
                  <dd class="content-data">
                    {{
                      currencyMoney(
                        org_invoice.amount,
                        org_invoice.currency.symbol
                      )
                    }}
                  </dd>
                </div>
                <div class="sm:col-span-1">
                  <dt class="label-data">
                    {{ $t("model.org_invoice.scheduled_payment_at") }}
                  </dt>
                  <dd class="content-data">
                    {{ dateFormatter(org_invoice.scheduled_payment_at) }}
                  </dd>
                </div>
                <div class="sm:col-span-1">
                  <dt class="label-data">
                    {{ $t("model.org_invoice.amount_paid") }}
                  </dt>
                  <dd class="content-data">
                    {{
                      currencyMoney(
                        org_invoice.amount_paid,
                        org_invoice.currency.symbol
                      )
                    }}
                  </dd>
                </div>
                <div class="sm:col-span-1">
                  <dt class="label-data">
                    {{ $t("model.org_invoice.org_invoice_discounts") }}
                  </dt>
                  <dd class="content-data">
                    {{ org_invoice.org_invoice_sum_discounts.join(", ") }}
                  </dd>
                </div>
                <div class="sm:col-span-1">
                  <dt class="label-data">
                    {{ $t("model.org_invoice.to_paid") }}
                  </dt>
                  <dd class="content-data">
                    {{
                      currencyMoney(
                        org_invoice.to_paid,
                        org_invoice.currency.symbol
                      )
                    }}
                  </dd>
                  <span
                    v-if="org_invoice.org_invoice_sum_discounts.length > 1"
                    class="text-red-500"
                  >
                    Sin descuentos en otra moneda
                  </span>
                </div>

                <div class="col-span-2">
                  <div
                    class="table-first-container"
                    v-if="org_invoice.invoice_items.length > 0"
                  >
                    <div class="table-second-container">
                      <div class="table-third-container">
                        <table class="table">
                          <thead class="table-header">
                            <tr>
                              <th scope="col" class="table-header-cell">
                                {{ $t("model.org_invoice_item.quantity") }}
                              </th>
                              <th scope="col" class="table-header-cell">
                                {{ $t("model.org_invoice_item.description") }}
                              </th>
                              <th scope="col" class="table-header-cell">
                                {{ $t("model.org_invoice_item.unit_price") }}
                              </th>
                              <th scope="col" class="table-header-cell">
                                {{ $t("model.org_invoice_item.amount") }}
                              </th>
                            </tr>
                          </thead>
                          <tbody class="table-body">
                            <tr
                              class="table-row"
                              v-for="(
                                invoice_item, index
                              ) in org_invoice.invoice_items"
                              :key="index"
                            >
                              <td class="table-cell">
                                {{ numberWithPrecision(invoice_item.quantity) }}
                              </td>
                              <td class="table-cell">
                                {{ invoice_item.description }}
                              </td>
                              <td class="table-cell">
                                {{
                                  currencyMoney(
                                    invoice_item.unit_price,
                                    org_invoice.currency.symbol
                                  )
                                }}
                              </td>
                              <td class="table-cell">
                                {{
                                  currencyMoney(
                                    invoice_item.amount,
                                    org_invoice.currency.symbol
                                  )
                                }}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <alert-warning
                    v-else
                    :warning_data="{
                      title: $t('labels.not_containers')
                    }"
                  />
                </div>
              </div>
            </div>

            <div class="panel-modal-footer">
              <button
                type="button"
                class="panel-modal-footer-close"
                @click="open = false"
              >
                Cerrar
              </button>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { ref } from "vue";
import {
  Dialog,
  DialogOverlay,
  TransitionChild,
  TransitionRoot
} from "@headlessui/vue";

export default {
  name: "AEFinancesQueryContainerOutcomesOrgInvoiceDetails",
  components: {
    Dialog,
    DialogOverlay,
    TransitionChild,
    TransitionRoot
  },
  props: {
    org_invoice: { type: Object, required: true },
    icon: {},
    link_color: {}
  },
  setup() {
    const open = ref(false);

    return { open };
  }
};
</script>
